import {UserDashboardBase} from "@bounce/shared/consumer/declarations";
import NiceModal from "@ebay/nice-modal-react";
import dayjs from "dayjs";
import {useCallback, useMemo} from "react";
import {Pages} from "~/bi-events/enum";
import {useAmplitude} from "~/contexts";
import RescheduleLimitDialog from "../components/dialogs/RescheduleLimitDialog";
import ReschedulePlanDialog from "../components/dialogs/ReschedulePlanDialog";
import RescheduleUnavailableDialog from "../components/dialogs/RescheduleUnavailableDialog";

interface UseReschedulePlanProps {
  userDashboard: UserDashboardBase | undefined;
  paymentPlanId: string | undefined;
  accountId: string;
  page: Pages;
  onClose?: () => void;
}

export const useReschedulePlan = ({userDashboard, paymentPlanId, accountId, page, onClose}: UseReschedulePlanProps) => {
  const {pageClickEvent} = useAmplitude();

  const shouldDisplayReschedulePlan = useMemo(() => {
    if (!userDashboard) return false;
    const frequency = userDashboard.installment_frequency;
    if (["non_linear", "first_and_fifteenth"].includes(frequency)) return false;
    return userDashboard.reschedule_block_reason !== "last_installment";
  }, [userDashboard]);

  const handleReschedulePlan = useCallback(async () => {
    pageClickEvent({page, cta: "reschedule_plan"});
    onClose?.();

    if (!userDashboard || !paymentPlanId) return;

    const installmentList = userDashboard.installment_list || [];
    const frequency = userDashboard.installment_frequency;

    if (!userDashboard.can_reschedule) {
      if (userDashboard.reschedule_block_reason === "six_month_limit") {
        await NiceModal.show(RescheduleLimitDialog);
      } else {
        await NiceModal.show(RescheduleUnavailableDialog);
      }
      return;
    }

    const nextUnpaidInstallment = installmentList.find(
      (inst) => inst.installment_status === "unpaid" || inst.installment_status === "late"
    );
    const nextPaymentDate = nextUnpaidInstallment
      ? dayjs(nextUnpaidInstallment.due_date).startOf("day")
      : dayjs().startOf("day");

    await NiceModal.show(ReschedulePlanDialog, {
      frequency: frequency as any,
      installmentAmountCents: userDashboard.installment_size_cents,
      originalDueDate: nextPaymentDate,
      paymentPlanId,
      accountId,
    });
  }, [userDashboard, paymentPlanId, accountId, page, onClose, pageClickEvent]);

  return {handleReschedulePlan, shouldDisplayReschedulePlan};
};
